import {createSelector} from '@reduxjs/toolkit';
import groupby from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';
import {DateTime} from 'luxon';

export const getAreaCoverage = state => state.areaCoverage;
export const getActiveResult = state =>
  state.results.resultsById[state.sidebar.activeResultId];

export const areaCoverageFilter = (result, coverage) => {
  const min = coverage[0] * 100;
  const max = coverage[1] * 100;

  return result.properties.coverage >= min && result.properties.coverage <= max;
};

export const filterByAreaCoverage = (results, coverage) =>
  results.filter(r => areaCoverageFilter(r, coverage));

export const getItemsGroupedByCollect = createSelector(
  [getActiveResult],
  activeResult => {
    if (!activeResult) {
      return [];
    }
    // Group by the Planet strip_id
    // or by satellite_id for non Planet sensors (Landsat, Sentinel)
    const groupByKey = item =>
      item.properties.strip_id
        ? item.properties.strip_id
        : item.properties.satellite_id;

    const groups = groupby(activeResult.properties.items, groupByKey);

    return Object.keys(groups).map(key => ({
      id: key,
      features: groups[key],
    }));
  },
);

export const getScenesSortDirection = state =>
  state.sidebar.scenesSortDirection;

export const getSortedGroupItems = createSelector(
  [getItemsGroupedByCollect, getScenesSortDirection],
  (itemGroups, sortDirection) => {
    const things = orderBy(
      itemGroups,
      itemGroup => {
        return Date.parse(itemGroup.features[0].properties.acquired);
      },
      sortDirection,
    );
    return things;
  },
);

export const selectFilteredResultIds = createSelector(
  [state => state.results.resultsById, getAreaCoverage],
  (results, areaCoverage) => {
    const filteredIds = Object.keys(results)?.filter(id =>
      areaCoverageFilter(results[id], areaCoverage),
    );
    return filteredIds;
  },
);

export const selectFilteredResultsList = createSelector(
  [selectFilteredResultIds, state => state.results.resultsById],
  (resultIds, results) => resultIds?.map(id => results[id]) || [],
);

export const getResultsById = state => state.results.resultsById;

export const getSelectedViewItems = state => state.results.viewItems;

export const getOrderableSelectedItems = state => state.results.orderItems;

export const getSelectedViewResults = createSelector(
  [state => state.results.viewResults, getResultsById],
  (selectedResultsIds, resultsById) =>
    orderBy(
      selectedResultsIds.map(id => resultsById[id]),
      'properties.acquired',
      'desc',
    ),
);

// Get the First and Last date for selected results shown on the map
export const getSelectedResultsDateRange = createSelector(
  [getSelectedViewResults],
  selectedResults => {
    const dates = selectedResults.map(result =>
      DateTime.fromISO(result.properties.acquired, {zone: 'utc'}),
    );

    if (dates.length) {
      const first = DateTime.min(...dates);
      const last = DateTime.max(...dates);
      if (first === last) {
        return [first.toISO()];
      } else {
        return [first.toISO(), last.toISO()];
      }
    }

    return [];
  },
);
